import {
  Box,
  Button,
  Flex,
  HStack,
  Text,
  VStack,
  useClipboard,
} from '@chakra-ui/react';
import { Trans, useTranslation } from 'react-i18next';

import { ArrowRightIcon } from 'components/uikit';
import { DynamicCopyIcon } from 'components/uikit/CopyWrapper';
import { InputType } from 'utils/analytics';

interface VndAnInstructionsProps {
  amount: string;
  currency: string;
  accountNumber: string;
  swiftCode: string;
  recipientName?: string;
  bankLogo?: { url: string; alt: string };
  bankLink?: { labelKey: string; onClick: () => void };
  isBranded: boolean;
  onInputCopied: (inputName: InputType) => void;
}

interface CopyValue {
  value: string;
  displayedValue?: string;
  inputType: InputType;
  className?: string;
}

interface Step {
  key: string;
  copies?: CopyValue[];
}

export function CopyChip({
  value,
  displayedValue,
  inputType,
  className,
  onInputCopied,
}: CopyValue & { onInputCopied: (inputName: InputType) => void }) {
  const { onCopy, hasCopied } = useClipboard(value);

  return (
    <HStack
      as='button'
      type='button'
      display='inline-flex'
      alignItems='center'
      spacing='0'
      gap='6px'
      h='auto'
      maxW='100%'
      mt='6px'
      px='10px'
      py='3px'
      bg='grey.white'
      borderWidth='1px'
      borderColor={hasCopied ? 'success.button' : 'grey.border'}
      borderRadius='8px'
      whiteSpace='normal'
      textAlign='start'
      className={className}
      _hover={{ borderColor: 'grey.primaryText' }}
      transition='border-color 0.2s'
      _focus={{ outline: 'none' }}
      onClick={(e) => {
        e.stopPropagation();
        onInputCopied(inputType);
        onCopy();
      }}
      aria-label={`Copy ${displayedValue ?? value}`}
    >
      <Text
        as='span'
        fontSize={['md', 'lg']}
        fontWeight='600'
        color='grey.primaryText'
        wordBreak='break-all'
      >
        {displayedValue ?? value}
      </Text>
      <DynamicCopyIcon
        hasCopied={hasCopied}
        boxSize='16px'
        color={hasCopied ? 'success.button' : 'grey.primaryText'}
      />
    </HStack>
  );
}

export function VndAnInstructions({
  amount,
  currency,
  accountNumber,
  swiftCode,
  recipientName,
  bankLogo,
  bankLink,
  isBranded,
  onInputCopied,
}: VndAnInstructionsProps) {
  const { t } = useTranslation('payments');

  const steps: Step[] = [
    { key: 'vnd_an_step_transfer' },
    { key: 'vnd_an_step_country' },
    { key: 'vnd_an_step_account_type' },
    {
      key: 'vnd_an_step_account_number',
      copies: [
        {
          value: accountNumber,
          inputType: 'credentials',
          className: 'ph-no-capture',
        },
      ],
    },
    {
      key: 'vnd_an_step_swift',
      copies: [{ value: swiftCode, inputType: 'bank' }],
    },
    {
      // The bank asks for the recipient name on the same screen as the amount
      key: recipientName
        ? 'vnd_an_step_recipient_amount'
        : 'vnd_an_step_amount',
      copies: [
        ...(recipientName
          ? [{ value: recipientName, inputType: 'recipient' as const }]
          : []),
        {
          value: amount,
          displayedValue: `${amount} ${currency}`,
          inputType: 'amount',
        },
      ],
    },
    { key: 'vnd_an_step_send' },
  ];

  return (
    <Box
      w='100%'
      p={['16px', '20px']}
      bg='payment.qrBackground'
      borderWidth={isBranded ? '1px' : '0'}
      borderColor='payment.qrBorder'
      borderRadius='13px'
      mb='20px'
    >
      <HStack
        justifyContent='space-between'
        alignItems='center'
        mb={['12px', '16px']}
      >
        <Text fontWeight='600' fontSize={['md', 'lg']} color='grey.primaryText'>
          {t('vnd_an_instructions_title')}
        </Text>
        {bankLogo && !bankLink ? (
          <Box
            as='img'
            src={bankLogo.url}
            alt={bankLogo.alt}
            w='70px'
            h='auto'
            flexShrink={0}
          />
        ) : null}
      </HStack>
      {bankLink && bankLogo ? (
        <HStack
          w='100%'
          justifyContent='space-between'
          alignItems='center'
          gap='10px'
          mb={['12px', '16px']}
        >
          <Text
            fontWeight='500'
            fontSize='md'
            color='grey.primaryText'
            lineHeight='1.4'
            flex='1'
          >
            <Trans
              ns='payments'
              i18nKey={bankLink.labelKey}
              components={{
                bold: <Text as='span' fontWeight='700' />,
              }}
            />
          </Text>
          <ArrowRightIcon
            color='grey.primaryText'
            boxSize='20px'
            flexShrink={0}
          />
          <Button
            onClick={bankLink.onClick}
            variant='unstyled'
            p='0'
            h='auto'
            w='auto'
            minW='auto'
            display='flex'
            alignItems='center'
            flexShrink={0}
            _hover={{ opacity: 0.8 }}
            _active={{ opacity: 0.6 }}
            _focus={{ boxShadow: 'none' }}
          >
            <Box
              as='img'
              src={bankLogo.url}
              alt={bankLogo.alt}
              w='70px'
              h='auto'
            />
          </Button>
        </HStack>
      ) : null}
      <VStack
        as='ol'
        align='stretch'
        spacing={['10px', '12px']}
        m='0'
        listStyleType='none'
      >
        {steps.map(({ key, copies }, index) => (
          <HStack as='li' key={key} align='flex-start' spacing='12px'>
            <Flex
              flexShrink={0}
              w='24px'
              h='24px'
              borderRadius='full'
              bg='payment.timeBar'
              color='payment.timeBarText'
              fontSize='sm'
              fontWeight='600'
              alignItems='center'
              justifyContent='center'
            >
              {index + 1}
            </Flex>
            <Box minW='0'>
              <Text
                fontWeight='400'
                fontSize={['md', 'lg']}
                color='grey.primaryText'
                lineHeight='24px'
              >
                <Trans
                  ns='payments'
                  i18nKey={key}
                  components={{
                    bold: <Text as='span' fontWeight='700' />,
                  }}
                />
              </Text>
              {copies ? (
                <Flex wrap='wrap' columnGap='8px'>
                  {copies.map((copy) => (
                    <CopyChip
                      key={copy.inputType}
                      {...copy}
                      onInputCopied={onInputCopied}
                    />
                  ))}
                </Flex>
              ) : null}
            </Box>
          </HStack>
        ))}
      </VStack>
    </Box>
  );
}
